import { useCallback, useMemo, useRef } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import BodyText from 'LuxKit/Typography/BodyText'
import CircleCheckboxInput from 'LuxKit/Checkbox/CircleCheckboxInput'
import RadioButton from 'LuxKit/Radio/RadioButton'
import Group from 'components/utils/Group'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import IconButton from 'LuxKit/Button/IconButton'
import noop from 'lib/function/noop'
import type { CardOption, CardCategory } from './CardsList'
import { SelectableCard } from './SelectableCard'
import { useCardSelection } from './useCardSelection'
import ImageCard from './ImageCard'

const ContentArea = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(12)};
  flex: 1;
  padding: ${rem(12)} ${rem(16)};
`

interface Props {
  options: Array<CardOption>
  onSelectionChange: (selectedTitles: Array<string>) => void
  select: 'single' | 'multiple'
  disabled?: boolean
  selection?: Array<string>
  categories: 'locations' | 'places'
  onInfoClick?: (option: CardOption, category: CardCategory) => void
}

function ImageCardsList({
  options,
  onSelectionChange,
  select,
  disabled = false,
  selection,
  categories,
  onInfoClick = noop,
}: Props) {
  const { selectedIndices, optionClickHandlers } = useCardSelection({
    options,
    selection,
    disabled,
    select,
    onSelectionChange,
  })

  const onInfoClickRef = useRef(onInfoClick)
  onInfoClickRef.current = onInfoClick

  const cardClickHandlers = useMemo(() =>
    options.map((option) => () => {
      onInfoClickRef.current(option, categories)
    }),
  [options, categories])

  const handleSelectionClick = useCallback((event: React.MouseEvent<HTMLInputElement>) => {
    event.stopPropagation()
  }, [])

  const handleSelectionChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const indexStr = event.currentTarget.dataset.optionIndex
    if (indexStr === undefined) return
    const index = parseInt(indexStr, 10)
    if (isNaN(index)) return
    optionClickHandlers[index]?.()
  }, [optionClickHandlers])

  if (options.length === 0) {
    return null
  }

  return (
    <Group direction="vertical" gap={12} fullWidth>
      {options.map((option, index) => {
        const isSelected = selectedIndices.has(index)
        let selectionControl: React.ReactNode

        if (select === 'multiple') {
          selectionControl = (
            <CircleCheckboxInput
              checked={isSelected}
              disabled={disabled}
              data-option-index={index}
              onClick={handleSelectionClick}
              onChange={handleSelectionChange}
            />
          )
        } else {
          selectionControl = (
            <RadioButton
              checked={isSelected}
              disabled={disabled}
              data-option-index={index}
              onClick={handleSelectionClick}
              onChange={handleSelectionChange}
            />
          )
        }

        return (
          <SelectableCard
            key={index}
            role={select === 'single' ? 'radio' : 'checkbox'}
            aria-checked={isSelected}
            disabled={disabled}
            onClick={cardClickHandlers[index]}
            className={clsx('with-image', { selected: isSelected })}
          >
            <ImageCard option={option} category={categories} />
            <ContentArea>
              <Group direction="vertical" gap={2} fullWidth>
                <Group direction="horizontal" gap={4} verticalAlign="center">
                  <BodyText variant="medium" colour="neutral-one" weight="bold">
                    {option.title}
                  </BodyText>
                  <IconButton
                    kind="tertiary"
                    size="small"
                    onClick={cardClickHandlers[index]}
                  >
                    <LineInfoCircleIcon />
                  </IconButton>
                </Group>
                {option.description && (
                  <BodyText variant="medium" colour="neutral-two">
                    {option.description}
                  </BodyText>
                )}
              </Group>
              {selectionControl}
            </ContentArea>
          </SelectableCard>
        )
      })}
    </Group>
  )
}

export default ImageCardsList
